import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { CardSpacer } from "@dashboard/components/CardSpacer";
import { Multiselect } from "@dashboard/components/Combobox";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import FormSpacer from "@dashboard/components/FormSpacer";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import { Savebar } from "@dashboard/components/Savebar";
import { WsmErrorFragment } from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useShippingZonesSearch from "@dashboard/searches/useShippingZonesSearch";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Checkbox, Input, Text, Textarea } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import { getWsmComposeErrorMessage, getWsmComposeFieldError, getWsmComposeFormErrors } from "../../errors";
import { complianceMessages, sectionMessages } from "../../messages";
import { WsmComplianceFormData } from "../../types";
import { wsmComplianceListPath } from "../../urls";
import { formatStateCodes, formatZoneCountries, parseStateCodes } from "../../utils";
import { WsmRail, WsmRailRow } from "../WsmRail/WsmRail";
import WsmYesNo from "../WsmYesNo/WsmYesNo";

export interface WsmCompliancePageProps {
  title: string;
  productName?: string | null;
  /** Zones already on the row, so a saved one shows even before a search runs. */
  knownZones: Array<{
    id: string;
    name: string;
    countries?: Array<{ country: string }> | null;
  }>;
  initialData: WsmComplianceFormData;
  errors: WsmErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: WsmComplianceFormData) => void;
  onDelete?: () => void;
}

export const WsmCompliancePage = ({
  title,
  productName,
  knownZones,
  initialData,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: WsmCompliancePageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<WsmComplianceFormData>(initialData);
  // The typed text, kept separate from the parsed codes so a half-typed "CA, N"
  // is not normalised out from under the merchant mid-keystroke.
  const [statesText, setStatesText] = useState(formatStateCodes(initialData.restrictedStates));
  const { search: searchZones, result: zonesResult } = useShippingZonesSearch({
    variables: { after: null, first: 20, query: "" },
  });
  const change = (patch: Partial<WsmComplianceFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const searched = (mapEdgesToItems(zonesResult.data?.search) ?? []).map(zone => ({
    label: zone.name,
    value: zone.id,
  }));
  const zoneOptions = [
    ...knownZones
      .filter(zone => !searched.some(option => option.value === zone.id))
      .map(zone => ({ label: zone.name, value: zone.id })),
    ...searched,
  ];
  const statesError = getWsmComposeFieldError(errors, "restrictedStates");
  const prop65TextError = getWsmComposeFieldError(errors, "prop65Text");
  const formErrors = getWsmComposeFormErrors(errors, intl);
  // Only the saved zones carry their countries; a zone picked in this session
  // has come back from a search that asks for id and name only.
  const chosenKnownZones = knownZones.filter(zone =>
    data.includeShippingZones.includes(zone.id),
  );
  const chosenZoneNames = data.includeShippingZones
    .map(id => zoneOptions.find(option => option.value === id)?.label ?? id)
    .join(", ");

  return (
    <DetailPageLayout>
      <TopNav href={wsmComplianceListPath} title={title} />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="wsm-compliance-warnings">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(complianceMessages.warningTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text key={message} size={3} color="critical1" data-test-id="wsm-compliance-error">
                {message}
              </Text>
            ))}
            <Checkbox
              data-test-id="wsm-compliance-prop65"
              disabled={disabled}
              name="prop65"
              checked={data.prop65}
              onCheckedChange={checked => change({ prop65: !!checked })}
            >
              <Text size={3}>{intl.formatMessage(complianceMessages.fieldProp65)}</Text>
            </Checkbox>
            <FormSpacer />
            {/* The standard Prop 65 short form runs to about forty words, and
                a merchant editing one in a single-line field can see eight. */}
            <Textarea
              rows={3}
              label={intl.formatMessage(complianceMessages.fieldProp65Text)}
              data-test-id="wsm-compliance-prop65-text"
              // Wording only matters when the warning is on.
              disabled={disabled || !data.prop65}
              value={data.prop65Text}
              onChange={event => change({ prop65Text: event.target.value })}
              error={!!prop65TextError}
              helperText={
                getWsmComposeErrorMessage(prop65TextError, intl) ??
                intl.formatMessage(complianceMessages.fieldProp65TextHelp)
              }
            />
          </DashboardCard.Content>
        </DashboardCard>
        <CardSpacer />
        <DashboardCard data-test-id="wsm-compliance-shipping">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(complianceMessages.restrictionTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Input
              label={intl.formatMessage(complianceMessages.fieldRestrictedStates)}
              data-test-id="wsm-compliance-states"
              disabled={disabled}
              value={statesText}
              onChange={event => {
                setStatesText(event.target.value);
                change({ restrictedStates: parseStateCodes(event.target.value) });
              }}
              error={!!statesError}
              helperText={
                getWsmComposeErrorMessage(statesError, intl) ??
                intl.formatMessage(complianceMessages.fieldRestrictedStatesHelp)
              }
            />
            <FormSpacer />
            <Multiselect
              name="includeShippingZones"
              label={intl.formatMessage(complianceMessages.fieldZones)}
              data-test-id="wsm-compliance-zones"
              disabled={disabled}
              options={zoneOptions}
              value={data.includeShippingZones.map(
                id =>
                  zoneOptions.find(option => option.value === id) ?? { label: id, value: id },
              )}
              loading={zonesResult.loading}
              fetchOptions={searchZones}
              onChange={event =>
                change({
                  includeShippingZones: ((event.target.value ?? []) as Array<
                    string | { value: string }
                  >).map(entry => (typeof entry === "string" ? entry : entry.value)),
                })
              }
              helperText={intl.formatMessage(complianceMessages.fieldZonesHelp)}
              alwaysFetchOnFocus
            />
            {data.includeShippingZones.length > 0 && (
              <Box
                paddingTop={2}
                display="grid"
                gap={1}
                data-test-id="wsm-compliance-zone-countries"
              >
                {chosenKnownZones.map(zone => (
                  <Text key={zone.id} size={2} color="default2">
                    {intl.formatMessage(
                      formatZoneCountries(zone)
                        ? complianceMessages.zoneCountries
                        : complianceMessages.zoneCountriesNone,
                      { name: zone.name, countries: formatZoneCountries(zone) },
                    )}
                  </Text>
                ))}
                <Text size={2} color="default2">
                  {intl.formatMessage(complianceMessages.zoneCountriesHelp)}
                </Text>
              </Box>
            )}
            <FormSpacer />
            {/* Same shape of text as the warning: a sentence a shopper reads. */}
            <Textarea
              rows={3}
              label={intl.formatMessage(complianceMessages.fieldRestrictionMessage)}
              data-test-id="wsm-compliance-message"
              disabled={disabled}
              value={data.restrictionMessage}
              onChange={event => change({ restrictionMessage: event.target.value })}
              helperText={intl.formatMessage(complianceMessages.fieldRestrictionMessageHelp)}
            />
          </DashboardCard.Content>
        </DashboardCard>
      </DetailPageLayout.Content>

      <DetailPageLayout.RightSidebar>
        <WsmRail
          title={intl.formatMessage(sectionMessages.railWhatItCovers)}
          testId="wsm-compliance-rail"
        >
          <WsmRailRow label={intl.formatMessage(sectionMessages.product)}>
            {productName || intl.formatMessage(sectionMessages.railProductUnset)}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(complianceMessages.colProp65)}>
            <WsmYesNo value={data.prop65} tone="warning" />
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(complianceMessages.colRestrictedStates)}>
            {formatStateCodes(data.restrictedStates) ||
              intl.formatMessage(complianceMessages.none)}
          </WsmRailRow>
          <WsmRailRow label={intl.formatMessage(complianceMessages.colZones)}>
            {chosenZoneNames || intl.formatMessage(complianceMessages.noZoneLimit)}
          </WsmRailRow>
        </WsmRail>
      </DetailPageLayout.RightSidebar>

      <Savebar>
        {onDelete && <Savebar.DeleteButton onClick={onDelete} />}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(wsmComplianceListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled}
          data-test-id="wsm-compliance-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default WsmCompliancePage;
